import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setViewingUserTrips } from '../../../../store/Actions';
import Slider from '@mui/material/Slider';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import Cropper from 'react-easy-crop';
import { FancyButton, FancyIconButton } from '../../../common/buttons';
import { FancyTextField } from '../../../common/inputs';
import { deleteUserTrip, storeUserTrip, tripIsInEvents } from '../../../../firebase/Firestore';
import { storeFile } from '../../../../firebase/CloudStorage';
import './EditTrip.scss';

const COVER_ASPECT = 16 / 10;
const COVER_MIN_ZOOM = 0.2;
const COVER_MAX_ZOOM = 5;
const COVER_ZOOM_STEP = 0.1;

const storeFileAsync = (filename, file) => new Promise((resolve, reject) => {
    storeFile(filename, file, resolve, null, reject);
});

const createImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
});

const getCroppedCoverBlob = async (imageSrc, croppedAreaPixels) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const sourceX = Math.max(0, croppedAreaPixels.x);
    const sourceY = Math.max(0, croppedAreaPixels.y);
    const sourceWidth = Math.min(
        image.naturalWidth - sourceX,
        croppedAreaPixels.width + Math.min(0, croppedAreaPixels.x)
    );
    const sourceHeight = Math.min(
        image.naturalHeight - sourceY,
        croppedAreaPixels.height + Math.min(0, croppedAreaPixels.y)
    );

    if(sourceWidth <= 0 || sourceHeight <= 0){
        return await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', 0.95);
        });
    }

    context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        sourceX - croppedAreaPixels.x,
        sourceY - croppedAreaPixels.y,
        sourceWidth,
        sourceHeight
    );

    return await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
    });
}

const numbersAreClose = (a, b) => Math.abs((a ?? 0) - (b ?? 0)) < 0.001;
const clampCoverZoom = (zoom) => Math.min(COVER_MAX_ZOOM, Math.max(COVER_MIN_ZOOM, zoom));
const cropValuesAreEqual = (a, b) => Boolean(
    a &&
    b &&
    numbersAreClose(a.x, b.x) &&
    numbersAreClose(a.y, b.y)
);
const areaValuesAreEqual = (a, b) => Boolean(
    a &&
    b &&
    numbersAreClose(a.x, b.x) &&
    numbersAreClose(a.y, b.y) &&
    numbersAreClose(a.width, b.width) &&
    numbersAreClose(a.height, b.height)
);

export function EditTrip() {
    const userTrips = useSelector(state=>state.viewingUserTrips);
    const dialogData = useSelector(state=>state.dialogData);
    const trip = dialogData.trip;
    const dispatch = useDispatch();

    const nameInputRef = useRef(null);
    const fileInputRef = useRef(null);

    const [name, setName] = useState(trip.name || '');
    const [coverPicUrl, setCoverPicUrl] = useState(trip.coverPicUrl ?? null);
    const [coverPicOriginalUrl, setCoverPicOriginalUrl] = useState(trip.coverPicOriginalUrl ?? trip.coverPicUrl ?? null);
    const [coverStorageId] = useState(trip.coverStorageId ?? trip.id ?? `${trip.userId}_${Date.now()}`);
    const coverOptions = trip.coverPicOptions;
    const [coverCrop, setCoverCrop] = useState(coverOptions && coverOptions.crop ? coverOptions.crop : { x: 0, y: 0 });
    const [coverZoom, setCoverZoom] = useState(coverOptions && coverOptions.zoom ? coverOptions.zoom : 1);
    const [coverCroppedArea, setCoverCroppedArea] = useState(coverOptions && coverOptions.croppedArea ? coverOptions.croppedArea : null);
    const [coverCroppedAreaPixels, setCoverCroppedAreaPixels] = useState(coverOptions && coverOptions.croppedAreaPixels ? coverOptions.croppedAreaPixels : null);
    const [coverDirty, setCoverDirty] = useState(false);
    const [coverUploadStatus, setCoverUploadStatus] = useState('idle');
    const [validationErrors, setValidationErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const dialogTitle = dialogData.dialogTitle || (trip.id ? 'Edit Trip' : 'New Trip');

    const markDirty = useCallback(() => setIsDirty(true), []);

    // ─── Auto-focus the name field for new trips ───
    useEffect(() => {
        if(trip.id) return;
        const t = setTimeout(() => nameInputRef.current?.focus(), 120);
        return () => clearTimeout(t);
    }, [trip.id]);

    const validateTrip = () => {
        const errors = {};
        if(!name || !name.trim()) errors.name = 'Enter a name';
        if(coverUploadStatus === 'uploading') errors.uploads = 'Wait for the cover photo to finish uploading';
        return errors;
    }

    const errorCount = useMemo(
        () => Object.values(validationErrors).filter(Boolean).length,
        [validationErrors]
    );

    const cancelEdit = () => {
        if(isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        // 'View Trip' is no longer a dialog — the trip detail page lives in the
        // URL now, so just close the modal in that case.
        if(!dialogData.parentDialog || dialogData.parentDialog === 'View Trip'){
            dispatch(setDialogOpen(''));
        } else {
            dispatch(setDialogOpen(dialogData.parentDialog, dialogData));
        }
    }

    const onSave = async () => {
        if(isSaving) return;
        const nextErrors = validateTrip();
        setValidationErrors(nextErrors);
        if(Object.values(nextErrors).some(Boolean)) return;

        setIsSaving(true);
        try {
            let nextCoverPicUrl = coverPicUrl;
            if(coverPicOriginalUrl && coverCroppedAreaPixels && (coverDirty || !nextCoverPicUrl)){
                const croppedCoverBlob = await getCroppedCoverBlob(coverPicOriginalUrl, coverCroppedAreaPixels);
                nextCoverPicUrl = await storeFileAsync(`${coverStorageId}_TripCoverPic`, croppedCoverBlob);
                // Cache-buster: the Storage path is stable across edits, so the URL
                // string would otherwise be identical to the previous cover and
                // browsers would keep showing the cached image until a hard refresh.
                nextCoverPicUrl = `${nextCoverPicUrl}&t=${Date.now()}`;
                setCoverPicUrl(nextCoverPicUrl);
            }

            const updatedTrip = await storeUserTrip(trip.id, {
                ...trip,
                name,
                coverPicUrl: nextCoverPicUrl,
                coverPicOriginalUrl,
                coverStorageId,
                coverPicOptions: coverPicOriginalUrl ? {
                    crop: coverCrop,
                    zoom: coverZoom,
                    croppedArea: coverCroppedArea,
                    croppedAreaPixels: coverCroppedAreaPixels
                } : null
            });
            dispatch(setViewingUserTrips({...userTrips, [updatedTrip.id]: updatedTrip}));

            if(!dialogData.parentDialog || dialogData.parentDialog === 'View Trip'){
                dispatch(setDialogOpen(''));
            } else {
                dispatch(setDialogOpen(dialogData.parentDialog));
            }
        } catch (error) {
            setValidationErrors({
                ...nextErrors,
                uploads: 'Cover photo upload failed. Please try again before saving.'
            });
        } finally {
            setIsSaving(false);
        }
    }

    const performDelete = async () => {
        if(await tripIsInEvents(trip)){
            alert('This trip exists in some of your events');
            setConfirmingDelete(false);
            return;
        }
        await deleteUserTrip(trip.id);
        const updatedTrips = {...userTrips};
        delete updatedTrips[trip.id];
        dispatch(setViewingUserTrips(updatedTrips));
        dispatch(setDialogOpen(dialogData.listDialog === null ? '' : (dialogData.listDialog || 'Trips')));
    }

    // Refs to latest closures so the global keydown listener never goes stale.
    const saveRef = useRef();
    const cancelRef = useRef();
    saveRef.current = onSave;
    cancelRef.current = cancelEdit;

    useEffect(() => {
        const handler = (e) => {
            if((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')){
                e.preventDefault();
                saveRef.current?.();
                return;
            }
            if(e.key === 'Escape'){
                if(document.querySelector('.MuiPopover-root, .MuiMenu-root')) return;
                cancelRef.current?.();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Cover photo file handling — used by file input, drag-drop, and clipboard paste.
    const handleCoverFile = useCallback(async (file) => {
        if(!file) return;
        if(!file.type || !file.type.startsWith('image/')) return;
        setCoverUploadStatus('uploading');
        setValidationErrors((prev) => prev.uploads ? {...prev, uploads: null} : prev);
        try {
            const url = await storeFileAsync(`${coverStorageId}_TripCoverPicOriginal`, file);
            // Cache-buster: the Storage path is stable, so re-uploading the
            // original would resolve to the same URL string and the browser
            // would keep serving the previously cached image.
            setCoverPicOriginalUrl(`${url}&t=${Date.now()}`);
            setCoverPicUrl(null);
            setCoverCrop({ x: 0, y: 0 });
            setCoverZoom(1);
            setCoverCroppedArea(null);
            setCoverCroppedAreaPixels(null);
            setCoverDirty(true);
            setCoverUploadStatus('idle');
            markDirty();
        } catch (error) {
            setCoverUploadStatus('error');
            setValidationErrors((prev) => ({...prev, uploads: 'Cover photo upload failed. Please try again.'}));
        }
    }, [coverStorageId, markDirty]);

    const onSelectCoverPic = (event) => {
        const file = event.currentTarget.files[0];
        event.currentTarget.value = '';
        handleCoverFile(file);
    }

    const onDropCover = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        if(file) handleCoverFile(file);
    }

    // ─── Clipboard paste → cover ───
    useEffect(() => {
        const handler = (e) => {
            // Skip when the user is pasting into a contenteditable / rich-text
            // editor — that paste belongs to the editor.
            const target = e.target;
            if(target && (target.isContentEditable || target.closest?.('[contenteditable="true"]'))) return;
            const items = e.clipboardData?.items;
            if(!items) return;
            for(const item of items){
                if(item.kind === 'file' && item.type?.startsWith('image/')){
                    const file = item.getAsFile();
                    if(file){
                        e.preventDefault();
                        handleCoverFile(file);
                        return;
                    }
                }
            }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [handleCoverFile]);

    const clearCoverPic = () => {
        setCoverPicOriginalUrl(null);
        setCoverPicUrl(null);
        setCoverCrop({ x: 0, y: 0 });
        setCoverZoom(1);
        setCoverCroppedArea(null);
        setCoverCroppedAreaPixels(null);
        setCoverDirty(true);
        markDirty();
    }

    const onCoverCropChange = useCallback((nextCrop) => {
        setCoverCrop((previousCrop) => {
            if(cropValuesAreEqual(previousCrop, nextCrop)) return previousCrop;
            setCoverDirty(true);
            setIsDirty(true);
            return nextCrop;
        });
    }, []);

    const onCoverCropComplete = useCallback((nextCroppedArea, nextCroppedAreaPixels) => {
        setCoverCroppedArea((previousArea) => areaValuesAreEqual(previousArea, nextCroppedArea) ? previousArea : nextCroppedArea);
        setCoverCroppedAreaPixels((previousAreaPixels) => areaValuesAreEqual(previousAreaPixels, nextCroppedAreaPixels) ? previousAreaPixels : nextCroppedAreaPixels);
    }, []);

    const onCoverZoomChange = useCallback((nextZoom) => {
        const clampedZoom = clampCoverZoom(nextZoom);
        setCoverZoom((previousZoom) => {
            if(numbersAreClose(previousZoom, clampedZoom)) return previousZoom;
            setCoverDirty(true);
            setIsDirty(true);
            return clampedZoom;
        });
    }, []);

    const adjustCoverZoom = useCallback((amount) => {
        onCoverZoomChange(coverZoom + amount);
    }, [coverZoom, onCoverZoomChange]);

    const resetCoverCrop = useCallback(() => {
        setCoverCrop({ x: 0, y: 0 });
        onCoverZoomChange(1);
        setCoverDirty(true);
        markDirty();
    }, [onCoverZoomChange, markDirty]);

    const uploadBlockMessage = coverUploadStatus === 'uploading'
        ? 'Cover photo is still uploading'
        : validationErrors.uploads;
    const saveIsBlocked = coverUploadStatus === 'uploading' || isSaving;

    return (
        <div className="et-page">
            {/* ───────── Sticky top bar ───────── */}
            <header className="et-topbar">
                <div className="et-topbar__left">
                    <FancyButton variant="ghost" onClick={cancelEdit} startIcon={<CloseIcon fontSize="small" />}>
                        Cancel
                    </FancyButton>
                </div>
                <div className="et-topbar__center">
                    <h1 className="et-topbar__title">{dialogTitle}</h1>
                    {isDirty && (
                        <span className="et-topbar__dirty" title="You have unsaved changes" aria-label="Unsaved changes">
                            <span className="et-topbar__dirty-dot" />
                            Unsaved
                        </span>
                    )}
                </div>
                <div className="et-topbar__right">
                    {errorCount > 0 && (
                        <span className="et-topbar__errors" role="status">
                            {errorCount} {errorCount === 1 ? 'issue' : 'issues'}
                        </span>
                    )}
                    <FancyButton
                        variant="dark"
                        onClick={onSave}
                        loading={saveIsBlocked}
                        disabled={saveIsBlocked}
                        title="Save trip (⌘S)"
                    >
                        Save
                    </FancyButton>
                </div>
            </header>

            {/* ───────── Scrollable body ───────── */}
            <div className="et-body">
                {/* Name card */}
                <section className="et-card">
                    <div className="et-card__header">
                        <div className="et-card__heading">
                            <LuggageOutlinedIcon className="et-card__icon" />
                            <h3 className="et-card__title">Trip name</h3>
                        </div>
                    </div>
                    <FancyTextField
                        label="Name"
                        fullWidth
                        inputRef={nameInputRef}
                        value={name}
                        error={Boolean(validationErrors.name)}
                        helperText={validationErrors.name || ''}
                        onChange={(event) => {
                            setName(event.target.value);
                            markDirty();
                            if(validationErrors.name) setValidationErrors({...validationErrors, name: null});
                        }}
                    />
                </section>

                {/* Cover photo card */}
                <section className="et-card et-cover-card">
                    <div className="et-card__header">
                        <div className="et-card__heading">
                            <ImageOutlinedIcon className="et-card__icon" />
                            <h3 className="et-card__title">Cover photo</h3>
                        </div>
                        {coverPicOriginalUrl && (
                            <FancyIconButton
                                ariaLabel="Remove cover photo"
                                size="sm"
                                shape="square"
                                variant="ghost"
                                onClick={clearCoverPic}
                            >
                                <CloseIcon fontSize="small" />
                            </FancyIconButton>
                        )}
                    </div>

                    <div
                        className={`et-cover-area ${isDragging ? 'is-dragover' : ''} ${coverPicOriginalUrl ? 'has-image' : 'is-empty'}`}
                        onDragOver={(e) => { e.preventDefault(); if(!isDragging) setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={onDropCover}
                    >
                        {coverPicOriginalUrl ? (
                            <Cropper
                                image={coverPicOriginalUrl}
                                crop={coverCrop}
                                zoom={coverZoom}
                                aspect={COVER_ASPECT}
                                minZoom={COVER_MIN_ZOOM}
                                maxZoom={COVER_MAX_ZOOM}
                                restrictPosition={false}
                                showGrid={false}
                                onCropChange={onCoverCropChange}
                                onCropComplete={onCoverCropComplete}
                                onZoomChange={onCoverZoomChange}
                            />
                        ) : (
                            <button
                                type="button"
                                className="et-cover-empty"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PhotoCameraOutlinedIcon className="et-cover-empty__icon" />
                                <div className="et-cover-empty__title">Drop, paste, or click</div>
                                <div className="et-cover-empty__sub">Drag a file, ⌘V from clipboard, or upload</div>
                            </button>
                        )}
                    </div>

                    <div className="et-cover-actions">
                        <FancyButton
                            onClick={() => fileInputRef.current?.click()}
                            loading={coverUploadStatus === 'uploading'}
                            disabled={isSaving}
                            startIcon={<PhotoCameraOutlinedIcon fontSize="small" />}
                            fullWidth
                        >
                            {coverPicOriginalUrl ? 'Replace photo' : 'Upload photo'}
                        </FancyButton>
                    </div>

                    {coverPicOriginalUrl && (
                        <div className="et-cover-tools">
                            <div className="et-cover-tool">
                                <div className="et-cover-tool__row">
                                    <span className="et-cover-tool__label">Zoom</span>
                                    <span className="et-cover-tool__value">{Math.round(coverZoom * 100)}%</span>
                                </div>
                                <div className="et-cover-zoom">
                                    <FancyIconButton
                                        ariaLabel="Zoom out"
                                        size="sm"
                                        shape="square"
                                        variant="secondary"
                                        disabled={numbersAreClose(coverZoom, COVER_MIN_ZOOM)}
                                        onClick={() => adjustCoverZoom(-COVER_ZOOM_STEP)}
                                    >
                                        <RemoveIcon fontSize="small"/>
                                    </FancyIconButton>
                                    <Slider
                                        className="et-cover-zoom__slider"
                                        min={COVER_MIN_ZOOM}
                                        max={COVER_MAX_ZOOM}
                                        step={COVER_ZOOM_STEP}
                                        value={coverZoom}
                                        onChange={(_, value) => onCoverZoomChange(value)}
                                        aria-label="Cover photo zoom"
                                    />
                                    <FancyIconButton
                                        ariaLabel="Zoom in"
                                        size="sm"
                                        shape="square"
                                        variant="secondary"
                                        disabled={numbersAreClose(coverZoom, COVER_MAX_ZOOM)}
                                        onClick={() => adjustCoverZoom(COVER_ZOOM_STEP)}
                                    >
                                        <AddIcon fontSize="small"/>
                                    </FancyIconButton>
                                    <FancyIconButton
                                        ariaLabel="Reset crop"
                                        size="sm"
                                        shape="square"
                                        variant="ghost"
                                        onClick={resetCoverCrop}
                                    >
                                        <RestartAltIcon fontSize="small"/>
                                    </FancyIconButton>
                                </div>
                            </div>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{display:'none'}}
                        onChange={onSelectCoverPic}
                    />
                </section>
            </div>

            {/* ───────── Sticky bottom bar ───────── */}
            <footer className="et-bottombar">
                <div className="et-bottombar__left">
                    {trip.id && !confirmingDelete && (
                        <FancyButton
                            variant="danger"
                            onClick={() => setConfirmingDelete(true)}
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                        >
                            Delete
                        </FancyButton>
                    )}
                    {trip.id && confirmingDelete && (
                        <div className="et-delete-confirm" role="alertdialog" aria-label="Confirm delete">
                            <span className="et-delete-confirm__msg">Permanently delete this trip?</span>
                            <FancyButton variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                                Cancel
                            </FancyButton>
                            <FancyButton variant="danger" size="sm" onClick={performDelete}>
                                Yes, delete
                            </FancyButton>
                        </div>
                    )}
                </div>
                <div className="et-bottombar__right">
                    {uploadBlockMessage && (
                        <span className="et-bottombar__status et-bottombar__status--warn">
                            {uploadBlockMessage}
                        </span>
                    )}
                </div>
            </footer>
        </div>
    );
}
